import _ from 'lodash';
import { useMutation } from 'react-query';
import FileSaver from 'file-saver';
import { toastr } from 'react-redux-toastr';

import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import {
  useFetchConditionsForApprovalOptions,
  useFetchScreeningLetterOptions,
} from '../../../containers/ApplicationProfile/LeaseApplicationTab/CreditCriminalStatus/AdverseActionLetterModal/hooks';
import DocumentService from '../../../services/documentService';

import { SCREENING_METHOD_STATUS } from '../ProviderScreening/constants';

const ADVERSE_LETTER_APPROVED_WITH_CONDITIONS_FILENAME =
  'Adverse Action Letter - Approved with conditions.pdf';
const ADVERSE_LETTER_DECLINED_FILENAME = 'Adverse Action Letter - Denied.pdf';

const INCOMPLETE_STATUSES = [
  SCREENING_METHOD_STATUS.NOT_STARTED,
  SCREENING_METHOD_STATUS.PENDING,
  SCREENING_METHOD_STATUS.NOT_READY,
];

export const toMethodKey = (name) => `${name.toLowerCase()}History`;

export const useCreateAdverseActionLetterModal = ({
  open,
  promptToaster,
  adverseActionLetterApprovalConditions,
  screeningLetterTypes,
  applicationId,
  organizationId,
  propertyId,
  handleClose,
  screeningData: { screeningMethods = [], screeningStatus },
}) => {
  const adverseScreeningMethods = screeningMethods.filter(
    (m) => !INCOMPLETE_STATUSES.includes(m.status),
  );

  const defaultValues = adverseScreeningMethods.reduce(
    (acc, m) => {
      acc[toMethodKey(m.name)] = true;
      return acc;
    },
    { other: false, otherScreeningOptions: [] },
  );

  const reactHookFormProps = useReactHookFormProps({
    shouldUnregister: true,
    defaultValues,
  });
  const { watch } = reactHookFormProps;
  const { isLoading: isLoadingScreeningLetterOptions, screeningLetterOptions } =
    useFetchScreeningLetterOptions(open, promptToaster);
  const {
    isLoading: isLoadingConditionsForApproval,
    conditionsForApprovalOptions,
  } = useFetchConditionsForApprovalOptions(open, promptToaster);
  const showConditionsForApproval =
    adverseActionLetterApprovalConditions &&
    screeningStatus === 'approvedWithConditions';

  const otherCheckboxSelected = watch('other');
  const selectedConditionForApproval = watch('conditionsForApproval');
  const conditionsForApprovalAmount = watch('conditionsForApprovalAmount');
  const otherScreeningOptions = watch('otherScreeningOptions');

  const methodCheckboxValues = adverseScreeningMethods.map((m) => ({
    key: toMethodKey(m.name),
    checked: watch(toMethodKey(m.name)),
  }));
  const anyMethodChecked = methodCheckboxValues.some((m) => m.checked === true);
  const allCheckboxesButOtherDeselected = !anyMethodChecked;
  const otherOptionsSelected =
    otherCheckboxSelected === true && otherScreeningOptions?.length > 0;
  const selectedValueNeedsAmount = conditionsForApprovalOptions.find(
    (condition) => condition.id === selectedConditionForApproval,
  )?.requiresAmount;

  const values = reactHookFormProps.getValues();

  const { saveAdverseActionLetter, isLoading: isSavingAdverseActionLetter } =
    useSaveAdverseActionLetter({
      values,
      adverseScreeningMethods,
      applicationId,
      screeningLetterTypes,
      organizationId,
      propertyId,
      screeningStatus,
      handleClose,
    });

  const isLoading =
    isLoadingScreeningLetterOptions || isLoadingConditionsForApproval;
  return {
    reactHookFormProps,
    isLoading,
    screeningLetterOptions,
    conditionsForApprovalOptions,
    showConditionsForApproval,
    adverseScreeningMethods,
    otherCheckboxSelected,
    selectedValueNeedsAmount,
    allCheckboxesButOtherDeselected,
    otherOptionsSelected,
    saveAdverseActionLetter,
    isSavingAdverseActionLetter,
    conditionsForApprovalAmount,
  };
};

const useSaveAdverseActionLetter = ({
  values,
  adverseScreeningMethods,
  applicationId,
  screeningLetterTypes,
  organizationId,
  propertyId,
  screeningStatus,
  handleClose,
}) => {
  const { fileName, letterType } = parseLetterTypeAndFileName(
    screeningStatus,
    screeningLetterTypes,
  );

  const methodHistoryValues = adverseScreeningMethods.reduce((acc, m) => {
    const key = toMethodKey(m.name);
    acc[key] = _.isNil(values[key]) ? true : values[key];
    return acc;
  }, {});

  const payload = {
    applicationId,
    letterType,
    fileName,
    conditionsForApproval: values.conditionsForApproval,
    ...methodHistoryValues,
    other: values.other,
    otherScreeningOptions: values.otherScreeningOptions,
  };

  if (values.conditionsForApprovalAmount) {
    payload.conditionsforApprovalAmount = values.conditionsForApprovalAmount;
  }

  const saveAdverseActionLetterRequest = async () => {
    const documentService = new DocumentService();
    const response = await documentService.saveAdverseActionLetter(
      organizationId,
      propertyId,
      applicationId,
      letterType,
      payload,
    );
    FileSaver.saveAs(response, fileName);
  };
  const saveAdverseActionLetterMutation = useMutation(
    saveAdverseActionLetterRequest,
    {
      onSuccess: () => {
        handleClose();
        toastr.success(
          'Success',
          'Adverse Action Letter generated successfully',
        );
      },
      onError: (error) => {
        toastr.error('Error', error.toString());
      },
    },
  );
  return {
    saveAdverseActionLetter: saveAdverseActionLetterMutation.mutate,
    isLoading: saveAdverseActionLetterMutation.isLoading,
  };
};

const parseLetterTypeAndFileName = (
  screeningStatus: string,
  screeningLetterTypes: Array<any>,
): Object => {
  const approvedWithConditionsLetterType =
    screeningLetterTypes.find(
      (letter) =>
        letter.name === 'Adverse Action Letter - Approved with conditions',
    )?.id ?? '';
  const declinedLetterType =
    screeningLetterTypes.find(
      (letter) => letter.name === 'Adverse Action Letter - Denied',
    )?.id ?? '';

  // Approved with Conditions
  if (screeningStatus === 'approvedWithConditions') {
    return {
      fileName: ADVERSE_LETTER_APPROVED_WITH_CONDITIONS_FILENAME,
      letterType: approvedWithConditionsLetterType,
    };
    // Declined
  } else if (screeningStatus === 'declined') {
    return {
      fileName: ADVERSE_LETTER_DECLINED_FILENAME,
      letterType: declinedLetterType,
    };
  }
  return {
    fileName: '',
    letterType: '',
  };
};
